import React, { useEffect, useRef, useState } from 'react';
import MinusIcon from '@mui/icons-material/RemoveCircleOutline';
import DatePicker, { registerLocale } from 'react-datepicker';
import PlusIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Box, DialogTitle, Slider } from '@mui/material';
import AvatarEditor from 'react-avatar-editor';
import Resizer from 'react-image-file-resizer';
import DeleteIcon from '@mui/icons-material/Clear';
import Tooltip from '@mui/material/Tooltip';
import { enGB } from 'date-fns/locale';
import cs from 'classnames';
import 'dayjs/locale/en-gb';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/DriveFileRenameOutline';

import 'react-datepicker/dist/react-datepicker.css';
registerLocale('en-GB', enGB);

import { deleteOrgAvatarById, patchOrganization, patchUploadOrgAvatarById } from '../../../api/organizationsApi';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import UploadFileIcon from '../../../assets/icons/upload-icon-file.svg';
import { useAppDispatch } from '../../../store/hooks';
import OrgUserInput from '../CreateOrganizationModal/OrgUserInput';
import { NOTIFICATION, RolesLowerCase } from '../../../enums';
import { saveUser } from '../../../store/user/userSlice';
import CrossIcon from '../../../assets/icons/cross.svg';
import { ORGANIZATION } from '../../../enums/TYPES';
import styles from './EditOrganization.module.scss';
import RolePlate from '../../modules/RolePlate';
import { getMyUserInfo } from '../../../api';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';
import { useHotkeys } from 'react-hotkeys-hook';

type TEditOrganizationModal = {
  open: boolean;
  switchModal: () => void;
  organization: ORGANIZATION;
  fetchAll?: () => Promise<unknown>;
};

dayjs.locale('en-gb');

const MAX_HOURS_LIMIT = 9999999;

const CustomInputWithRequired = React.forwardRef<HTMLInputElement, React.HTMLProps<HTMLInputElement>>((props, ref) => (
  <input {...props} ref={ref} required style={{ ...props.style, border: '1px solid gray' }} />
));

const handleNumberInputWheel = (event: React.WheelEvent<HTMLFormElement>) => {
  const target = event.target as HTMLInputElement;
  if (target.type === 'number') target.blur();
};

const EditOrganizationModal: React.FC<TEditOrganizationModal> = ({ open, switchModal, organization, fetchAll }) => {
  const notification = useSnackbar();

  const [totalHours, setTotalHours] = useState(0);
  const [orgField, setOrgFields] = useState({
    allowedVideoQualities: organization?.allowedVideoQualities,
    name: organization.name,
    description: organization.description,
    location: organization.location,
    users_limit: organization.users_limit,
    storage_limit: organization.storage_limit,
    id: organization.id,
    hours_limit: organization.hours_limit,
    hours_limit_used: organization.hours_limit_used,
    download_hours_limit: organization.download_hours_limit,
    download_hours_limit_used: organization.download_hours_limit_used,
    period_start: organization.period_start,
    period_end: organization.period_end,
    remaining_hours_from_previous_period: organization.remaining_hours_from_previous_period,
    reset_period_on_start_change: false,
  });

  const [newUsers, setNewUsers] = useState<{ email: string; username: string; id: number }[]>([]);

  const [periodStartCheckbox, setPeriodStartCheckbox] = useState(false);
  const [remainingHoursCheckbox, setRemainingHoursCheckbox] = useState(false);
  const [oaList, setOaList] = useState(organization?.users);
  const [loading, setLoading] = useState(false);
  const avatarSubmitButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [removeList, setRemoveList] = useState<{ id: string }[]>([]);

  const reset = () => {
    setNewUsers([]);
    setOrgFields({
      allowedVideoQualities: organization?.allowedVideoQualities,
      name: organization.name,
      description: organization.description,
      location: organization.location,
      users_limit: organization.users_limit,
      storage_limit: organization.storage_limit,
      id: organization.id,
      hours_limit: organization.hours_limit,
      hours_limit_used: organization.hours_limit_used,
      download_hours_limit: organization.download_hours_limit,
      download_hours_limit_used: organization.download_hours_limit_used,
      period_start: organization.period_start,
      period_end: organization.period_end,
      remaining_hours_from_previous_period: organization.remaining_hours_from_previous_period,
      reset_period_on_start_change: false,
    });
    setRemoveList([]);
    setOaList(organization?.users);
    setPeriodStartCheckbox(false);
    setRemainingHoursCheckbox(false);
    setTotalHours(0);
  };

  useEffect(() => {
    reset();
  }, [organization]);

  const dispatch = useAppDispatch();
  const onSubmit = async (e: any) => {
    if (e) {
      e?.preventDefault();
    }
    const dataSet = { ...orgField };
    // @ts-ignore
    if (dataSet?.users) {
      // @ts-ignore
      delete dataSet.users;
    }
    setLoading(true);
    try {
      const requestBody = {
        allowedVideoQualities: orgField?.allowedVideoQualities,
        name: orgField.name,
        period_start: orgField.period_start,
        period_end: orgField.period_end,
        remaining_hours_from_previous_period: +orgField.remaining_hours_from_previous_period,
        hours_limit: +totalHours || +orgField.hours_limit,
        download_hours_limit: +orgField.download_hours_limit,
        users_limit: +orgField.users_limit,
        storage_limit: +orgField.storage_limit,
        reset_period_on_start_change: orgField.reset_period_on_start_change,
        newUsers,
      };

      await patchOrganization(orgField.id, requestBody);
      if (imgFileToSubmit) {
        const formData = new FormData();
        formData.append('file', imgFileToSubmit, 'compressed-image.png');
        await patchUploadOrgAvatarById(orgField.id, formData);
      }
      if (fetchAll) {
        await fetchAll();
      }
      const userJson = await getMyUserInfo();
      dispatch(saveUser(userJson.data));
      onClose();
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    reset();
    setRemoveList([]);
    switchModal();
    setOaList(organization.users);
    setImageUrl(organization?.image_url);
    setIsFileUploaded(false);
    setShowUploadImg(false);
    setClick(false);
    setScale(1);
    setImgFileToSubmit(null);
    setShowDeletePreviousPeriodModal(false);
  };

  const removeNewUser = (id: number) => {
    const fitleredUsers = newUsers.filter((o) => o.id !== id);
    setNewUsers(fitleredUsers);
  };
  const addUser = () => {
    const users = [...newUsers];
    users.push({
      email: '',
      username: '',
      id: Date.now(),
    });
    setNewUsers(users);
  };
  const setNewUser = (data: { email: string; username: string; id: number }) => {
    const { id } = data;
    const userIndex = [...newUsers].findIndex((o) => o.id === id);
    if (userIndex !== -1) {
      const fields = [...newUsers];
      fields[userIndex] = {
        ...data,
      };
      setNewUsers(fields);
    }
  };

  const onOrgExpirationDateChange = (date: Date | null) => {
    if (!date) {
      setOrgFields({ ...orgField, period_end: '' });
      return;
    }

    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setOrgFields({ ...orgField, period_end: formattedDate });
  };

  const onOrgFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrgFields({
      ...orgField,
      [e.target.name]: e.target.value,
    });
  };

  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [showUploadImg, setShowUploadImg] = useState(false);
  const [imgUrl, setImageUrl] = useState(organization?.image_url);
  const [click, setClick] = useState(false);
  const [scale, setScale] = useState<number>(1);
  const switchUploadImgModal = () => {
    setShowUploadImg(!showUploadImg);
  };
  const openPhotoUploader = () => {
    inputRef?.current?.click();
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<AvatarEditor>(null);
  const onDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    const reader = new FileReader();
    const file = e.dataTransfer.files[0];
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        setImageUrl(img.src);
        setIsFileUploaded(true);
      };
    };
    reader.readAsDataURL(file);
  };
  const onDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const photofile = event.target?.files![0];

    const reader = new FileReader();

    reader.onloadend = () => {
      // the file's data URL is available in `reader.result`
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        setImageUrl(img.src);
        setIsFileUploaded(true);
      };
    };
    reader.readAsDataURL(photofile);
  };
  const handleScale = (event: Event, value: number | number[]) => {
    setScale(value as number);
    setClick(true);
  };
  const cancel = () => {
    setImageUrl(organization?.image_url);
    setImgFileToSubmit(null);
    setIsFileUploaded(false);
  };

  const [imgFileToSubmit, setImgFileToSubmit] = useState<Blob | null>(null);
  const submitImg = async () => {
    try {
      setLoading(true);
      if (editorRef.current) {
        const canvas = editorRef.current.getImage();
        const dataUrl = canvas.toDataURL();
        const blob = await fetch(dataUrl).then((res) => res.blob());
        setIsFileUploaded(!isFileUploaded);
        switchUploadImgModal();
        const compressedResult = await new Promise<Blob>((resolve) => {
          Resizer.imageFileResizer(
            blob,
            300,
            300,
            'JPEG',
            80,
            0,
            (uri) => {
              const resultBlob = fetch(uri as string).then((res) => res.blob());
              resolve(resultBlob);
            },
            'base64',
          );
        });
        setImageUrl(dataUrl);
        setImgFileToSubmit(compressedResult);
        notification.showMessage(NOTIFICATION.FILE_UPLOADED);
      }
    } catch (err) {
      notification.showMessage(NOTIFICATION.FILE_NOT_UPLOADED);
    } finally {
      setLoading(false);
    }
  };

  const [deleteModal, setDeleteModal] = useState(false);
  const switchDeleteImgModal = () => {
    setDeleteModal(!deleteModal);
  };

  const switchStartDate = () => {
    setPeriodStartCheckbox(!periodStartCheckbox);

    if (!periodStartCheckbox) {
      const formattedDate = dayjs(new Date()).format('YYYY-MM-DD');
      setOrgFields({
        ...orgField,
        period_start: formattedDate,
        hours_limit_used: 0,
        reset_period_on_start_change: true,
      });
    } else {
      setOrgFields({
        ...orgField,
        period_start: organization.period_start,
        hours_limit_used: organization.hours_limit_used,
        reset_period_on_start_change: false,
      });
      setTotalHours(0);
      setRemainingHoursCheckbox(false);
    }
  };

  const handleRemainingHoursCheckboxClick = () => {
    if (!remainingHoursCheckbox) {
      const totalHours = +orgField.hours_limit + +orgField.remaining_hours_from_previous_period.toFixed(2);
      if (totalHours > MAX_HOURS_LIMIT) {
        setTotalHours(MAX_HOURS_LIMIT);
      } else {
        setTotalHours(+totalHours.toFixed(2));
      }
    } else {
      setTotalHours(0);
    }

    setRemainingHoursCheckbox(!remainingHoursCheckbox);
  };

  const onDeleteOrgImg = async () => {
    try {
      if (organization.image_url) {
        setLoading(true);
        await deleteOrgAvatarById(organization.id);
        setImageUrl('');
        setImgFileToSubmit(null);
        if (fetchAll) {
          await fetchAll();
        }
        const userJson = await getMyUserInfo();
        dispatch(saveUser(userJson.data));
      } else {
        setImageUrl('');
        setImgFileToSubmit(null);
      }
      switchDeleteImgModal();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const switchAdditionalTranscoding = () => {
    setOrgFields({
      ...orgField,
      allowedVideoQualities: !orgField.allowedVideoQualities,
    });
  };

  const [showDeletePreviousPeriodModal, setShowDeletePreviousPeriodModal] = useState(false);
  const switchDeletePreviousPeriodModal = () => {
    setShowDeletePreviousPeriodModal(!showDeletePreviousPeriodModal);
  };

  const removeRemaningHours = () => {
    switchDeletePreviousPeriodModal();
    setOrgFields({
      ...orgField,
      remaining_hours_from_previous_period: 0,
    });
  };

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    if (showUploadImg && isFileUploaded) {
      avatarSubmitButtonRef?.current?.click();
      return;
    }

    submitButtonRef?.current?.click();
  });

  useEffect(() => {
    if (orgField.hours_limit > MAX_HOURS_LIMIT) {
      setOrgFields({ ...orgField, hours_limit: MAX_HOURS_LIMIT });
    }

    if (totalHours) {
      const totalHours = +orgField.hours_limit + +orgField.remaining_hours_from_previous_period.toFixed(2);

      if (totalHours > MAX_HOURS_LIMIT) {
        setTotalHours(MAX_HOURS_LIMIT);
      } else {
        setTotalHours(+totalHours.toFixed(2));
      }
    }
  }, [orgField.hours_limit, organization.hours_limit]);

  const [isEditingStart, setIsEditingStart] = useState(false);

  const startEditStart = () => setIsEditingStart(true);

  const onStartDatePicked = (date: Date | null) => {
    if (!date) return;
    setPeriodStartCheckbox(false);
    setOrgFields((prev) => ({
      ...prev,
      period_start: dayjs(date).format('YYYY-MM-DD'),
      // editing via pencil = NO RESET by default
      reset_period_on_start_change: false,
    }));
    setPeriodStartCheckbox(false);
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalTitle}>
        <DialogTitle fontSize={18} fontWeight={700}>
          Edit Organisation
        </DialogTitle>
      </div>

      <Modal open={showUploadImg} onClose={onClose} loading={loading}>
        {!isFileUploaded && (
          <div className={styles.uploadImgContainer}>
            <div className={styles.modalTitle}>
              Upload Avatar
              <img src={CrossIcon} alt="cross" onClick={switchUploadImgModal} />
            </div>

            <div onClick={openPhotoUploader} onDrop={onDrop} onDragOver={onDragOver} className={styles.photoUpload}>
              <div className={styles.innerUpload}>
                <input
                  max="5242880"
                  hidden
                  type="file"
                  ref={inputRef}
                  onChange={handleFileChange}
                  size={5e10}
                  accept="image/png, image/jpeg"
                />

                <img src={UploadFileIcon} alt="upload content " style={{ marginTop: '65px' }} />

                <div>
                  Upload designs by drag and drop or <span className={styles.text}> click to upload</span>.
                </div>
              </div>
            </div>
          </div>
        )}

        {isFileUploaded && (
          <div className={styles.uploadImgContainer}>
            <div className={styles.modalTitle} style={{ marginBottom: '20px' }}>
              Crop Avatar
              <img src={CrossIcon} alt="cross" onClick={switchModal} />
            </div>
            <div>
              <div>
                {imgUrl && (
                  <AvatarEditor ref={editorRef} image={imgUrl} width={300} height={300} scale={click ? scale : 1} borderRadius={400} />
                )}

                <Box>
                  <div className={styles.slider}>
                    <MinusIcon fontSize="small" className={styles.minusIcon} />

                    <Slider
                      sx={{
                        '& .MuiSlider-thumb': {
                          color: '#39B3EA',
                        },
                        '& .MuiSlider-track': {
                          color: '#39b3ea',
                        },
                        '& .MuiSlider-rail': {
                          color: '#acc4e4',
                        },
                        '& .MuiSlider-active': {
                          color: 'green',
                        },
                      }}
                      defaultValue={1}
                      min={1}
                      max={20}
                      aria-label="Default"
                      valueLabelDisplay="auto"
                      onChange={handleScale}
                    />
                    <PlusIcon fontSize="small" className={styles.plusIcon} />
                  </div>
                </Box>
              </div>
              <div className={styles.uploadImgBtns}>
                <OutlinedButton kind="default" onClick={cancel}>
                  Cancel
                </OutlinedButton>
                <ContainedButton onClick={submitImg}>
                  <div ref={avatarSubmitButtonRef}>Save avatar</div>
                </ContainedButton>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {organization && (
        <>
          <form onSubmit={onSubmit} onWheelCapture={handleNumberInputWheel} className={styles.form}>
            <div className={styles.uploadModalField}>
              <Avatar name={organization.name} className={styles.userEditAvatar} avatar={imgUrl} />

              <div className={styles.uploadBtn1}>
                <ContainedButton className={styles.contained} onClick={switchUploadImgModal}>
                  Upload New
                </ContainedButton>
              </div>
              <div className={styles.uploadBtn2}>
                <OutlinedButton kind="default" disabled={!imgUrl} onClick={switchDeleteImgModal}>
                  Delete
                </OutlinedButton>

                <Modal open={deleteModal} onClose={onClose} loading={loading}>
                  <div className={styles.deleteModalBody}>
                    <div className={styles.deleteModalTitle}>Are you sure u want to delete organisation image?</div>
                    <div className={styles.avatarContainer}>
                      <Avatar name={organization.name} className={styles.avatarToDelete} avatar={imgUrl} />
                    </div>
                    <ContainedButton disabled={loading} onClick={onDeleteOrgImg}>
                      Yes
                    </ContainedButton>
                    <OutlinedButton onClick={switchDeleteImgModal}>No</OutlinedButton>
                  </div>
                </Modal>
              </div>
            </div>

            <Tooltip title={' Allow additional video qualities '} placement="left">
              <div className={styles.fieldContainer} onClick={switchAdditionalTranscoding}>
                <div className={styles.notifyCheckBox}>
                  <div className={styles.checkboxWrapper30}>
                    <span className={styles.checkbox}>
                      <input type="checkbox" onChange={switchAdditionalTranscoding} checked={orgField.allowedVideoQualities} />
                      <svg>
                        <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                      </svg>
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                      <symbol id="checkbox-30" viewBox="0 0 22 22">
                        <path
                          fill="none"
                          stroke="currentColor"
                          d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                        />
                      </symbol>
                    </svg>
                    Additional Processing
                  </div>
                </div>
              </div>
            </Tooltip>

            <div className={styles.fieldContainer}>
              <span className={styles.label}>Name</span>
              <input
                name={'name'}
                onChange={onOrgFieldChange}
                required
                defaultValue={organization.name}
                className={styles.field}
                placeholder="Name"
              />
            </div>

            <div className={styles.fieldContainerLocation}>
              <span className={styles.labelLocation} style={{ color: 'white' }}>
                Location
              </span>
              <div className={styles.location}>{organization.location}</div>
            </div>

            <div className={styles.fieldContainer}>
              <span className={styles.label}>User Limit</span>
              <input
                required
                defaultValue={organization.users_limit}
                type="number"
                className={styles.field}
                placeholder="Users limit"
                name="users_limit"
                onChange={onOrgFieldChange}
              />
            </div>

            <div className={styles.fieldContainer}>
              <span className={styles.label}>Storage Limit (GB)</span>
              <input
                required
                defaultValue={organization.storage_limit}
                type="number"
                className={styles.field}
                placeholder="Storage limit"
                name="storage_limit"
                onChange={onOrgFieldChange}
              />
            </div>

            <div className={styles.fieldContainerLocation}>
              <span className={styles.labelLocation} style={{ color: 'white' }}>
                Storage used (GB)
              </span>
              <div className={styles.location} style={{ fontSize: '12px', marginTop: '3px', marginLeft: '3px' }}>
                {organization.storage_usage.toFixed(1)}
              </div>
            </div>

            <div className={totalHours ? styles.hoursLimitCalculator : ''}>
              <div className={styles.fieldContainer}>
                <span className={styles.label}>Hours Limit</span>
                <input
                  required
                  value={orgField?.hours_limit}
                  type="number"
                  className={cs(styles.field, totalHours ? styles.modifiedHoursLimit : '')}
                  placeholder="Hours limit"
                  name="hours_limit"
                  min="1"
                  maxLength={7}
                  onChange={onOrgFieldChange}
                />
              </div>
              {!!totalHours && (
                <>
                  <div className={cs(styles.fieldContainerLocation, styles.hoursLimitPrecalculation)}>
                    <span className={styles.labelLocation} style={{ color: 'white' }}>
                      Carry Over
                    </span>
                    <div className={styles.location} style={{ fontSize: '12px', marginTop: '4px', marginLeft: '3px' }}>
                      {orgField.remaining_hours_from_previous_period.toFixed(2)}
                    </div>
                  </div>
                  <div className={cs(styles.fieldContainerLocation, styles.hoursLimitPrecalculation)}>
                    <span className={styles.labelLocation} style={{ color: 'white' }}>
                      Total Hours
                    </span>
                    <div className={styles.location} style={{ fontSize: '12px', marginTop: '4px', marginLeft: '3px' }}>
                      {totalHours}
                    </div>
                  </div>
                </>
              )}
            </div>

            {(organization?.hours_limit_used > 0 || orgField?.hours_limit_used === 0) && (
              <div className={styles.fieldContainerLocation}>
                <span className={styles.labelLocation} style={{ color: 'white' }}>
                  Hours Limit used
                </span>
                <div className={styles.location} style={{ fontSize: '12px', marginTop: '3px', marginLeft: '3px' }}>
                  {orgField?.hours_limit_used?.toFixed(2)}
                </div>
              </div>
            )}

            <div className={styles.fieldContainer}>
              <span className={styles.label}>Download Hours Limit</span>
              <input
                required
                value={orgField?.download_hours_limit}
                type="number"
                className={styles.field}
                placeholder="Download hours limit"
                name="download_hours_limit"
                min="0"
                maxLength={7}
                onChange={onOrgFieldChange}
              />
            </div>

            {(organization?.download_hours_limit_used > 0 || orgField?.download_hours_limit_used === 0) && (
              <div className={styles.fieldContainerLocation}>
                <span className={styles.labelLocation} style={{ color: 'white' }}>
                  Download Hours Limit used
                </span>
                <div className={styles.location} style={{ fontSize: '12px', marginTop: '3px', marginLeft: '3px' }}>
                  {orgField?.download_hours_limit_used?.toFixed(2)}
                </div>
              </div>
            )}

            {periodStartCheckbox && orgField.remaining_hours_from_previous_period ? (
              <div className={styles.fieldContainerLocation}>
                <span className={styles.labelLocation} style={{ color: 'white' }}>
                  Remaining hours limit from previous period
                </span>
                <div className={styles.location} style={{ fontSize: '12px', marginTop: '3px', marginLeft: '3px' }}>
                  {orgField.remaining_hours_from_previous_period.toFixed(2)}
                  <Tooltip title={' Apply this to next period '} placement="top">
                    <div className={styles.remainingHoursCheckBox}>
                      <div className={styles.checkboxWrapper30}>
                        <span className={styles.checkbox}>
                          <input type="checkbox" onChange={handleRemainingHoursCheckboxClick} checked={remainingHoursCheckbox} />
                          <svg>
                            <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                          </svg>
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                          <symbol id="checkbox-30" viewBox="0 0 22 22">
                            <path
                              fill="none"
                              stroke="currentColor"
                              d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                            />
                          </symbol>
                        </svg>
                      </div>
                    </div>
                  </Tooltip>
                  <DeleteIcon onClick={switchDeletePreviousPeriodModal} className={styles.deleteIcon} />
                </div>
                {showDeletePreviousPeriodModal && (
                  <div className={styles.deletePrevHoursModal}>
                    <div>Are you sure you want to remove the hours limit from the previous period?</div>
                    <ContainedButton onClick={removeRemaningHours} kind="red">
                      Remove
                    </ContainedButton>
                    <OutlinedButton onClick={switchDeletePreviousPeriodModal} kind="default">
                      Close
                    </OutlinedButton>
                  </div>
                )}
              </div>
            ) : (
              <></>
            )}

            {orgField.period_start && (
              <div className={styles.fieldContainerLocation}>
                <span className={styles.labelLocation} style={{ color: 'white' }}>
                  Hours limit period start
                </span>

                {!isEditingStart ? (
                  <div
                    className={styles.location}
                    style={{ fontSize: '12px', marginTop: '3px', marginLeft: '3px', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {dayjs(orgField.period_start).format('DD-MM-YYYY')}
                    <Tooltip title={'Edit start period'} placement="top">
                      <button type="button" onClick={startEditStart} className={styles.editPeriodIcon}>
                        <EditIcon fontSize="small" />
                      </button>
                    </Tooltip>
                  </div>
                ) : (
                  <>
                    <div className={styles.fieldContainer}>
                      <span className={cs(styles.label, styles.endDateField)}>Hours limit period start</span>
                      <DatePicker
                        selected={
                          orgField.period_start
                            ? dayjs(orgField.period_start, 'YYYY-MM-DD').isValid()
                              ? dayjs(orgField.period_start, 'YYYY-MM-DD').toDate()
                              : null
                            : null
                        }
                        onChange={onStartDatePicked}
                        onCalendarClose={() => setIsEditingStart(false)}
                        onSelect={(d) => onStartDatePicked(d)}
                        dateFormat="dd-MM-yyyy"
                        locale="en-GB"
                        placeholderText="DD-MM-YYYY"
                        className={styles.field}
                        customInput={<CustomInputWithRequired />}
                      />
                    </div>
                  </>
                )}

                {/* your existing reset checkbox stays as-is below */}
                <Tooltip title={' Reset start period '} placement="top">
                  <div className={styles.periodEndCheckBox}>
                    <div className={styles.checkboxWrapper30}>
                      <span className={styles.checkbox}>
                        <input type="checkbox" onChange={switchStartDate} checked={periodStartCheckbox} />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                    </div>
                  </div>
                </Tooltip>
              </div>
            )}

            <div className={styles.fieldContainer}>
              <span className={cs(styles.label, styles.endDateField)}>Hours limit period end</span>
              <DatePicker
                selected={
                  orgField.period_end
                    ? dayjs(orgField.period_end, 'DD-MM-YYYY').isValid()
                      ? dayjs(orgField.period_end, 'DD-MM-YYYY').toDate()
                      : null
                    : null
                }
                onChange={onOrgExpirationDateChange}
                dateFormat="dd-MM-yyyy"
                locale="en-GB"
                placeholderText="DD-MM-YYYY"
                className={styles.field}
                minDate={dayjs().add(1, 'day').toDate()}
                customInput={<CustomInputWithRequired />}
              />
            </div>
            <button style={{ display: 'none' }}></button>
            <div className={styles.oaList}>
              {oaList
                ?.filter((u) => u?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN)
                .map((oa) => (
                  <div key={oa.id} className={styles.oaContainer}>
                    <div className={styles.oaElement}>
                      {oa.avatar_url ? <Avatar avatar={oa.avatar_url} /> : <div className={styles.oaAvatar}>{oa.email[0]}</div>}

                      <Tooltip title={oa.email}>
                        <div className={styles.oaEmail}>{oa.email}</div>
                      </Tooltip>
                    </div>
                    <div className={styles.oaElement}>
                      <Tooltip title={!oa.invitation_token ? 'Current Role' : 'Not active'}>
                        <div className={!oa.invitation_token ? styles.active : styles.notActive}>
                          <RolePlate className={styles.rolePlate} userRole={oa?.role?.title} />
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                ))}
            </div>
            <div>
              <div className={styles.oasFieldContainer}>
                {newUsers.map((field, index) => (
                  <OrgUserInput
                    deleteIconClass={styles.oasDeleteIconClass}
                    key={field.id}
                    index={index}
                    removeUser={removeNewUser}
                    data={field}
                    setUser={setNewUser}
                  />
                ))}
              </div>
              <div className={styles.actionBtns}>
                <button type="button" className={styles.addMore} onClick={addUser}>
                  + Add new organisation admin?
                </button>
                <ContainedButton disabled={loading} type="submit">
                  <div ref={submitButtonRef}>Update</div>
                </ContainedButton>

                <OutlinedButton type="button" kind="default" onClick={onClose}>
                  Cancel
                </OutlinedButton>
              </div>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default EditOrganizationModal;
